const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
const MEETING_NO_GROUP_SIZE = 3;
const MEETING_NO_LAST_GROUP_SIZE = 4;
const MEETING_NO_DISPLAY_SEPARATOR = '-';
/**
 * 会议号展示为 xxx-xxx-xxxx（10 位末组 4 位；9 位则为 xxx-xxx-xxx）。
 * 仅改展示，不改入库/入会用的纯数字。
 *
 * @param {string} [meetingNo] - 会议号.
 * @param {string} [separator] - 分组分隔符，首页列表用空格对齐参考图.
 * @returns {string}
 */
export function formatMeetingNo(meetingNo, separator = MEETING_NO_DISPLAY_SEPARATOR) {
    if (!meetingNo) {
        return '';
    }
    const digits = String(meetingNo).replace(/\D/g, '');
    if (!digits) {
        return meetingNo;
    }
    const groups = [];
    let remaining = digits.length;
    if (remaining > MEETING_NO_GROUP_SIZE
        && remaining % MEETING_NO_GROUP_SIZE === 1) {
        groups.unshift(digits.slice(remaining - MEETING_NO_LAST_GROUP_SIZE));
        remaining -= MEETING_NO_LAST_GROUP_SIZE;
    }
    while (remaining > 0) {
        const start = Math.max(0, remaining - MEETING_NO_GROUP_SIZE);
        groups.unshift(digits.slice(start, remaining));
        remaining = start;
    }
    return groups.join(separator);
}
/**
 * 参会时长格式化为 HH:MM:SS。
 */
export function formatDurationClock(seconds) {
    if (seconds == null || seconds < 0 || !Number.isFinite(seconds)) {
        return '--';
    }
    const total = Math.floor(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return [h, m, s].map(n => String(n).padStart(2, '0')).join(':');
}
function pad2(n) {
    return String(n).padStart(2, '0');
}
/**
 * 录制列表时长：不足 1 小时为 MM:SS，否则 HH:MM:SS。
 *
 * @param {number} [seconds] - 秒
 * @returns {string}
 */
export function formatRecordingDuration(seconds) {
    if (seconds == null || seconds < 0 || !Number.isFinite(seconds)) {
        return '';
    }
    const total = Math.floor(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) {
        return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
    }
    return `${pad2(m)}:${pad2(s)}`;
}
/**
 * 录制列表时间：MM/DD HH:mm。
 *
 * @param {string} [iso] - ISO 时间
 * @returns {string}
 */
export function formatRecordingListTime(iso) {
    if (!iso) {
        return '';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return `${pad2(date.getMonth() + 1)}/${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}
export const DEFAULT_RECORDING_LIST_FILTER = {
    endDate: '',
    scene: 'all',
    startDate: '',
    time: 'all'
};
const MS_PER_DAY = 24 * 60 * 60 * 1000;
/**
 * 是否已设置非默认筛选。
 */
export function isRecordingListFilterActive(filter) {
    return filter.scene !== 'all'
        || filter.time !== 'all'
        || Boolean(filter.startDate)
        || Boolean(filter.endDate);
}
/**
 * 录制是否命中列表筛选（会中=有会议号；会外=无会议号）。
 */
export function matchesRecordingListFilter(item, filter) {
    const inMeeting = Number(item.meetingId) > 0 || Boolean(item.meetingNo);
    if (filter.scene === 'in-meeting' && !inMeeting) {
        return false;
    }
    if (filter.scene === 'out-meeting' && inMeeting) {
        return false;
    }
    const created = item.createdAt ? new Date(item.createdAt).getTime() : Number.NaN;
    if (!Number.isFinite(created)) {
        return filter.time === 'all' && !filter.startDate && !filter.endDate;
    }
    const now = Date.now();
    if (filter.time === '1m' && created < now - (30 * MS_PER_DAY)) {
        return false;
    }
    if (filter.time === '3m' && created < now - (90 * MS_PER_DAY)) {
        return false;
    }
    if (filter.startDate) {
        const start = new Date(`${filter.startDate}T00:00:00`).getTime();
        if (Number.isFinite(start) && created < start) {
            return false;
        }
    }
    if (filter.endDate) {
        const end = new Date(`${filter.endDate}T23:59:59.999`).getTime();
        if (Number.isFinite(end) && created > end) {
            return false;
        }
    }
    return true;
}
/**
 * 本地日期时间（不含秒也可读）。
 */
export function formatDateTime(iso) {
    if (!iso) {
        return '';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleString();
}
/**
 * 仅时分，如 18:37。
 */
export function formatClockHm(iso) {
    if (!iso) {
        return '';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}
/**
 * 最近入会短格式：08/10 14:41。
 */
export function formatJoinShort(iso) {
    if (!iso) {
        return '--';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '--';
    }
    return `${pad2(date.getMonth() + 1)}/${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}
/**
 * 08月10日。
 */
export function formatMonthDay(iso) {
    if (!iso) {
        return '';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return `${pad2(date.getMonth() + 1)}月${pad2(date.getDate())}日`;
}
function formatGmtOffset(date) {
    const offsetMin = -date.getTimezoneOffset();
    const sign = offsetMin >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMin);
    const hh = pad2(Math.floor(abs / 60));
    const mm = pad2(abs % 60);
    return `GMT${sign}${hh}:${mm}`;
}
/**
 * 首页日期：8月14日。
 */
export function formatHomeTodayLabel(date = new Date()) {
    return `${date.getMonth() + 1}月${date.getDate()}日`;
}
/**
 * 首页星期：星期一。
 */
export function formatHomeWeekdayLabel(date = new Date()) {
    const names = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
    return names[date.getDay()];
}
/**
 * 列表日期分组标题：当天为「今天 8月17日」，否则「8月18日」。
 */
export function formatMeetingGroupDateLabel(iso, todayPrefix) {
    const date = iso ? new Date(iso) : new Date();
    if (Number.isNaN(date.getTime())) {
        return todayPrefix;
    }
    const monthDay = `${date.getMonth() + 1}月${date.getDate()}日`;
    const now = new Date();
    const isToday = date.getFullYear() === now.getFullYear()
        && date.getMonth() === now.getMonth()
        && date.getDate() === now.getDate();
    return isToday ? `${todayPrefix} ${monthDay}` : monthDay;
}
/**
 * 首页会议按开始日期分组（今天优先，随后按日期升序）。
 */
export function groupPortalMeetingsByDate(items, todayPrefix) {
    const groups = [];
    const index = new Map();
    for (const item of items) {
        const iso = item.scheduledStart || item.createdAt || item.sortTime;
        const date = iso ? new Date(iso) : null;
        const key = date && !Number.isNaN(date.getTime())
            ? `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
            : 'unknown';
        let group = index.get(key);
        if (!group) {
            group = {
                items: [],
                key,
                label: formatMeetingGroupDateLabel(iso, todayPrefix)
            };
            index.set(key, group);
            groups.push(group);
        }
        group.items.push(item);
    }
    groups.sort((a, b) => a.key.localeCompare(b.key));
    groups.forEach(group => {
        group.items.sort((a, b) => {
            const rank = (status) => (status === 'ONGOING' ? 0 : 1);
            const byStatus = rank(a.status) - rank(b.status);
            if (byStatus !== 0) {
                return byStatus;
            }
            const ta = new Date(a.scheduledStart || a.createdAt || a.sortTime || 0).getTime();
            const tb = new Date(b.scheduledStart || b.createdAt || b.sortTime || 0).getTime();
            return ta - tb;
        });
    });
    return groups;
}
/**
 * 时分区间：14:00-14:30。
 */
export function formatTimeRangeHm(startIso, endIso) {
    const start = formatClockHm(startIso);
    const end = formatClockHm(endIso);
    if (start && end) {
        return `${start}-${end}`;
    }
    return start;
}
/**
 * 详情日期：2026年08月14日。
 */
export function formatYearMonthDay(iso) {
    if (!iso) {
        return '';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return `${date.getFullYear()}年${pad2(date.getMonth() + 1)}月${pad2(date.getDate())}日`;
}
/**
 * GMT 偏移，如 GMT+08:00。
 */
export function formatGmtOffsetText(iso) {
    const date = iso ? new Date(iso) : new Date();
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return formatGmtOffset(date);
}
/**
 * 开始/结束间隔的分钟数。
 */
export function minutesBetween(startIso, endIso) {
    if (!startIso || !endIso) {
        return undefined;
    }
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
        return undefined;
    }
    return Math.round((end - start) / 60000);
}
/**
 * 详情页时间区间：08月10日14:36-15:36(GMT+08:00) 中国标准时间。
 */
export function formatMeetingTimeRange(item) {
    const startIso = item.scheduledStart || item.createdAt || item.joinedAt;
    const endIso = item.scheduledEnd || item.endedAt || item.leftAt;
    const start = startIso ? new Date(startIso) : null;
    const end = endIso ? new Date(endIso) : null;
    if (!start || Number.isNaN(start.getTime())) {
        return formatDateTime(item.joinedAt);
    }
    const datePart = `${pad2(start.getMonth() + 1)}月${pad2(start.getDate())}日`;
    const startHm = `${pad2(start.getHours())}:${pad2(start.getMinutes())}`;
    const gmt = formatGmtOffset(start);
    const tzHint = -start.getTimezoneOffset() === 8 * 60 ? ' 中国标准时间' : '';
    if (!end || Number.isNaN(end.getTime())) {
        return `${datePart}${startHm}(${gmt})${tzHint}`;
    }
    const endHm = `${pad2(end.getHours())}:${pad2(end.getMinutes())}`;
    return `${datePart}${startHm}-${endHm}(${gmt})${tzHint}`;
}
function dateGroupKey(iso) {
    if (!iso) {
        return '';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}
function dateGroupParts(iso) {
    if (!iso) {
        return { dateLabel: '', yearLabel: '' };
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return { dateLabel: '', yearLabel: '' };
    }
    return {
        dateLabel: `${date.getMonth() + 1}月${date.getDate()}日 ${WEEKDAYS[date.getDay()]}`,
        yearLabel: `${date.getFullYear()}年`
    };
}
/**
 * 按入会日期分组（倒序，接口已按 joinedAt desc）。
 */
export function groupHistoryByDate(items) {
    const groups = [];
    const index = new Map();
    for (const item of items) {
        const key = dateGroupKey(item.joinedAt) || 'unknown';
        let group = index.get(key);
        if (!group) {
            const parts = dateGroupParts(item.joinedAt);
            group = {
                dateLabel: parts.dateLabel || key,
                items: [],
                key,
                label: `${parts.dateLabel} ${parts.yearLabel}`.trim() || key,
                yearLabel: parts.yearLabel
            };
            index.set(key, group);
            groups.push(group);
        }
        group.items.push(item);
    }
    return groups;
}
/**
 * 按会议名称、会议号、发起人过滤。
 */
export function filterHistoryItems(items, keyword) {
    const q = keyword.trim().toLowerCase().replace(/\s+/g, '');
    if (!q) {
        return items;
    }
    return items.filter(item => {
        const title = (item.title || '').toLowerCase();
        const meetingNo = (item.meetingNo || '').replace(/\s+/g, '');
        const organizer = (item.organizerName || '').toLowerCase();
        return title.includes(q)
            || meetingNo.includes(q)
            || organizer.includes(keyword.trim().toLowerCase());
    });
}
/**
 * 仅未结束的会议可从历史重新入会；已结束不再提供入会入口。
 */
export function canRejoinHistory(item) {
    return Boolean(item.joinable && item.meetingId);
}
/**
 * 按 meetingId 取对应录像：优先可播放文件，其次最新一条。
 */
export function findRecordingForMeeting(recordings, meetingId) {
    if (!meetingId) {
        return undefined;
    }
    const matches = recordings.filter(item => item.meetingId === meetingId);
    if (!matches.length) {
        return undefined;
    }
    const playable = matches.filter(item => item.fileAvailable !== false);
    const pool = playable.length ? playable : matches;
    return pool.slice().sort((a, b) => {
        const ta = new Date(a.endedAt || a.createdAt).getTime();
        const tb = new Date(b.endedAt || b.createdAt).getTime();
        return tb - ta;
    })[0];
}
